'use client';

import { useEffect } from 'react';

export interface KeyboardNavigationConfig {
  onLeftArrow?: () => void;
  onRightArrow?: () => void;
  onEscape?: () => void;
  canGoLeft?: boolean;
  canGoRight?: boolean;
  canEscape?: boolean;
}

/**
 * Custom hook for keyboard navigation
 * Follows React best practices:
 * - Cleanup function removes event listeners
 * - Stable callback references prevent unnecessary re-renders
 * @see https://react.dev/reference/react/useEffect#adding-event-listeners
 */
export function useKeyboardNavigation({
  onLeftArrow,
  onRightArrow,
  onEscape,
  canGoLeft = true,
  canGoRight = true,
  canEscape = true,
}: KeyboardNavigationConfig): void {
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      switch (event.key) {
        case 'ArrowLeft':
          if (canGoLeft && onLeftArrow) {
            event.preventDefault();
            onLeftArrow();
          }
          break;
        case 'ArrowRight':
          if (canGoRight && onRightArrow) {
            event.preventDefault();
            onRightArrow();
          }
          break;
        case 'Escape':
          if (canEscape && onEscape) {
            event.preventDefault();
            onEscape();
          }
          break;
      }
    }

    document.addEventListener('keydown', handleKeyDown);

    // Cleanup function removes event listener on unmount and before re-run
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onLeftArrow, onRightArrow, onEscape, canGoLeft, canGoRight, canEscape]);
}
